import React, { useEffect, useMemo, useState } from 'react';
import '../../styles/components/ContributionHeatmap.scss';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_LABELS = ['', 'Mon', '', 'Wed', '', 'Fri', ''];

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const levelForMinutes = (minutes) => {
  if (!minutes) return 0;
  if (minutes < 15) return 1;
  if (minutes < 45) return 2;
  if (minutes < 90) return 3;
  return 4;
};

const buildWeeks = (year, minutesByDate) => {
  const jan1 = new Date(year, 0, 1);
  const dec31 = new Date(year, 11, 31);

  const start = new Date(jan1);
  start.setDate(start.getDate() - start.getDay()); // back up to the preceding Sunday

  const end = new Date(dec31);
  end.setDate(end.getDate() + (6 - end.getDay())); // forward to the following Saturday

  const weeks = [];
  let cursor = new Date(start);
  while (cursor <= end) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const dateStr = toDateStr(cursor);
      const inYear = cursor.getFullYear() === year;
      week.push({
        date: dateStr,
        inYear,
        minutes: minutesByDate[dateStr] || 0,
        isMonthStart: inYear && cursor.getDate() === 1,
        month: cursor.getMonth(),
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
};

const ContributionHeatmap = ({ username, token, isSelf }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [days, setDays] = useState([]);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!username) return;
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/users/${username}/heatmap?year=${year}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        setDays(data.days || []);
        if (data.availableYears?.length) setAvailableYears(data.availableYears);
        setStreak(data.currentStreak || 0);
      })
      .catch((err) => console.error('Failed to load activity heatmap:', err))
      .finally(() => setLoading(false));
  }, [username, token, year]);

  const minutesByDate = useMemo(() => {
    const map = {};
    days.forEach((d) => { map[d.date] = d.minutes; });
    return map;
  }, [days]);

  const weeks = useMemo(() => buildWeeks(year, minutesByDate), [year, minutesByDate]);

  const totalMinutes = days.reduce((sum, d) => sum + d.minutes, 0);
  const activeDays = days.filter((d) => d.minutes > 0).length;

  const monthMarkers = useMemo(() => {
    const markers = [];
    let lastMonth = -1;
    weeks.forEach((week, weekIdx) => {
      const monthStartDay = week.find((d) => d.isMonthStart);
      if (monthStartDay && monthStartDay.month !== lastMonth) {
        markers.push({ weekIdx, label: MONTH_LABELS[monthStartDay.month] });
        lastMonth = monthStartDay.month;
      }
    });
    return markers;
  }, [weeks]);

  if (!isSelf && activeDays === 0 && !loading) return null;

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-foreground">Activity</h3>
          {streak > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 text-[11px] font-bold">
              🔥 {streak} day{streak === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="text-xs bg-muted rounded-lg px-2 py-1 border-0 text-foreground focus:outline-none"
        >
          {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto pb-1">
        <div className="inline-flex flex-col gap-1" style={{ minWidth: weeks.length * 13 }}>
          <div className="flex gap-[3px] pl-6">
            {weeks.map((_, weekIdx) => {
              const marker = monthMarkers.find((m) => m.weekIdx === weekIdx);
              return (
                <div key={weekIdx} className="text-[9px] text-muted-foreground" style={{ width: 10 }}>
                  {marker ? marker.label : ''}
                </div>
              );
            })}
          </div>
          <div className="flex gap-[3px]">
            <div className="flex flex-col gap-[3px] pr-1" style={{ width: 20 }}>
              {DAY_LABELS.map((label, i) => (
                <div key={i} className="text-[9px] text-muted-foreground leading-none" style={{ height: 10 }}>{label}</div>
              ))}
            </div>
            {weeks.map((week, weekIdx) => (
              <div key={weekIdx} className="flex flex-col gap-[3px]">
                {week.map((day, dayIdx) => (
                  <div
                    key={dayIdx}
                    title={day.inYear ? `${day.date}: ${day.minutes} min` : ''}
                    className={`heatmap-cell level-${day.inYear ? levelForMinutes(day.minutes) : 'blank'}`}
                    style={{ width: 10, height: 10, borderRadius: 2 }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground mt-2">
        {loading ? 'Loading activity...' : `${totalMinutes} min studied across ${activeDays} active day${activeDays === 1 ? '' : 's'} in ${year}`}
      </p>
    </div>
  );
};

export default ContributionHeatmap;
